import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import Footer from "~/components/Footer";
import { useScrollReveal } from "~/lib/use-scroll-reveal";
import styles from "~/styles/about.module.css";

export const meta: MetaFunction = () => [
  { title: "About — Prodigium Dynamics" },
  {
    name: "description",
    content:
      "Prodigium Dynamics is an AI-driven studio building digital products, content systems, and connection layers for a multilingual world.",
  },
];

export default function AboutPage() {
  useScrollReveal();

  return (
    <>
      <section className={styles.hero}>
        <div>
          <p className={styles.heroLabel}>About Prodigium</p>
          <h1 className={styles.heroTitle}>BUILT<br />TO<br />LAST.</h1>
        </div>
        <div className={styles.heroRight}>
          <p className={styles.heroStatement}>
            <strong>Most things are made to be seen.</strong><br />
            We make things that are felt.<br />
            Ordinary is an option we don&apos;t consider.
          </p>
          <p className={styles.heroFounded}>Est. 2026 · Global Operations</p>
        </div>
      </section>

      <div className="rule" />

      <section className={`${styles.section} reveal`}>
        <p className={styles.sectionLabel}>What we are</p>
        <div className={styles.twoCol}>
          <h2 className={styles.sectionTitle}>An AI-driven studio that builds things that deserve to exist.</h2>
          <div className={styles.sectionBody}>
            <p>Prodigium Dynamics is a studio where technology meets intention. We operate quietly, build deliberately, and release only what deserves to exist.</p>
            <p>No noise. No excess. Just work that holds its ground.</p>
            <p>We don&apos;t start with technology. We start with the question no one is asking yet — then we build the answer before anyone knows they need it.</p>
          </div>
        </div>
      </section>

      <div className="rule" />

      <div className={styles.statsBand}>
        {[
          { n: "2", label: "Active Products" },
          { n: "3+", label: "In Development" },
          { n: "2026", label: "Founded" },
          { n: "∞", label: "Ambition" },
        ].map(({ n, label }) => (
          <div key={label} className={`${styles.statBlock} reveal`}>
            <div className={styles.statNumber}>{n}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div className="rule" />

      <section className={`${styles.section} reveal`}>
        <p className={styles.sectionLabel}>Principles</p>
        <h2 className={styles.sectionTitle}>How we operate.</h2>
        <div className={styles.principles}>
          {[
            { n: "01", title: "Precision over noise", text: "We don't ship features. We release convictions. Every decision is made deliberately, with full awareness of what it means and what it costs." },
            { n: "02", title: "Depth over reach", text: "We'd rather be exactly right for one person than approximately useful to many. Depth compounds. Reach dilutes." },
            { n: "03", title: "Evidence over assumption", text: "We don't speculate about what users need. We observe, test, and let the work speak. Assumptions are placeholders. Evidence is architecture." },
            { n: "04", title: "Silence before announcement", text: "We move in silence and release with intention. The work is always ahead of the announcement. We don't build in public — we build, then show." },
          ].map(({ n, title, text }) => (
            <div key={n} className={styles.principleItem}>
              <span className={styles.principleNum}>{n}</span>
              <p className={styles.principleTitle}>{title}</p>
              <p className={styles.principleText}>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <section className={`${styles.section} reveal`}>
        <p className={styles.sectionLabel}>Timeline</p>
        <h2 className={styles.sectionTitle}>The record so far.</h2>
        <div className={styles.timeline}>
          {[
            { date: "2026 Q1", title: "Prodigium Dynamics Founded", text: "Studio established with a single premise: build what others can only describe." },
            { date: "2026 Q2", title: "PageBurn Launched", text: "First public product. A reading environment built for how the brain actually works. Live at pageburn.site." },
            { date: "2026 Q2", title: "Skills Catalog Released", text: "Engineered system prompts for consistent AI output. Built in production, not theory." },
            { date: "2026 Q3", title: "Brubri — Beta", text: "Execution system for neurodivergent brains. Microtask fragmentation, focus modes, and neuro-state tracking." },
            { date: "——", title: "Naomi — In Development", text: "Connection layer. Not built to assist. Built to remain." },
          ].map(({ date, title, text }) => (
            <div key={title} className={styles.timelineItem}>
              <div className={styles.timelineDate}>{date}</div>
              <div className={styles.timelineLine} />
              <div>
                <p className={styles.timelineTitle}>{title}</p>
                <p className={styles.timelineText}>{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <div className={`${styles.ctaBand} reveal`}>
        <h2 className={styles.ctaTitle}>Building something that<br /><span>deserves to exist?</span></h2>
        <Link to="/contact" className="btn-primary">Let&apos;s talk →</Link>
      </div>

      <Footer />
    </>
  );
}
